/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Button,
  Avatar,
  Box,
  TextField,
  InputAdornment,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuList,
  ListItemIcon,
  ListItemText,
  IconButton,
  MenuItem,
  Alert,
  Paper
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { submitSell } from "./../helpers/payment";
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from "notistack";
import classNames from "classnames";
import styles from "./../sass/tradeWidget.module.scss";
import PurchasePreview from "./PurchasePreview";
import { ChevronDown, CloseCircleOutline } from "mdi-material-ui";
import { parseBackendErrors } from "./../helpers/parseBackendErrors";
import { SetBalance } from "./../store/actions/balance.js";
import { getBalances } from "./../helpers/balance";
import { useParams } from "react-router-dom";
import { CheckAdditionalDetails } from "./../store/actions/app.js";

const Sell = (props) => {
  // state
  const [is_error_visible, setErrorVisibile] = useState(false);
  const [error, setError] = useState("Something went wrong");
  const [balance, setBallance] = useState(0);

  // state
  const [submitting, setSubmitting] = useState(false);
  const [selectedToken, setSelectedToken] = useState("");
  const [tokenSelectorOpen, setTokenSelectorOpen] = useState(false);

  // redux
  const { token, loggedin, user } = useSelector((state) => state.app);
  const { list, loading } = useSelector((state) => state.assets);
  const { list: balanceList, loading: balanceLoading } = useSelector((state) => state.balance);
  const dispatch = useDispatch();

  // misc hooks
  const { handleSubmit, control, watch, errors, setValue } = useForm();
  const { enqueueSnackbar } = useSnackbar();
  const amount = watch("amount");
  const isValid = /^\d+(\.\d+)?$/i.test(amount);
  const params = useParams();

  // lifecycles
  useEffect(() => {
    if (!loading && list.length > 0) {
      if (params && params.asset_id) {
        const findAsset = list.find((asset) => {
          return asset.id === params.asset_id;
        });
        const assetToSelect = findAsset && findAsset;
        setSelectedToken(assetToSelect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, list]);

  useEffect(() => {
    if(selectedToken && params.asset_id && params.asset_id !== selectedToken.id) {
      props.history.push("/asset/" + selectedToken.id)
    } else if(selectedToken && loggedin) {
      const findBalance = balanceList.find(item => {
        return item.asset.symbol === selectedToken.symbol
      });
      setBallance(findBalance ? findBalance.amount : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedToken]);

  // methods
  const handleSubmitPayment = async (form) => {
    if (!loggedin) {
      enqueueSnackbar("Please login to continue", {
        variant: "warning",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      });
      return;
    } else if(!user.profile) {
      dispatch(CheckAdditionalDetails(true));
      return;
    }
    setSubmitting(true);

    const [err, response] = await submitSell(
      parseFloat(form.amount),
      selectedToken.id,
      token
    );
    if (err) {
      const parsed_errors = parseBackendErrors(err.response);
      setError(parsed_errors);
      setErrorVisibile(true);
      setSubmitting(false);
      return;
    }
    enqueueSnackbar("Success! Your balance should update momentarily", {
      variant: "success",
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
    });
    setError("");
    setErrorVisibile(false);
    setSubmitting(false);
    setValue("amount", "");
    handleGetBalances(token);
  };

  const handleTokenSelectorToggle = () => {
    setTokenSelectorOpen(!tokenSelectorOpen);
  };

  const handleGetBalances = async (token) => {
    const [err, response] = await getBalances(token);
    if (err) {
      dispatch(SetBalance([]));
      return;
    }
    dispatch(SetBalance(response.data));
  };

  return (
    <Paper className={classNames(styles["buy"], "p-3")}>
      <form onSubmit={handleSubmit(handleSubmitPayment)} className="w-100">
        <Controller
          as={
            <TextField
              label="Amount"
              autoComplete="new-password"
              fullWidth={true}
              variant="outlined"
              error={errors.amount && true}
              helperText={errors?.amount?.message}
              className="mb-3"
              inputProps={{ maxLength: 30 }}
              disabled={!selectedToken}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {selectedToken.display_symbol}{" "}
                    <Button
                      size="small"
                      className={classNames(styles["max"])}
                      disabled={!loggedin || !selectedToken}
                      onClick={() => {
                        setValue("amount", balance);
                      }}
                    >
                      Max
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          }
          name="amount"
          control={control}
          defaultValue={""}
          rules={{
            required: "This field is required.",
            pattern: {
              value: /^\d+(\.\d+)?$/i,
              message: "Please enter a valid amount (eg. 125.38).",
            },
            validate: (value) => {
              if (parseFloat(value) <= 0) {
                return "Please enter a valid amount (eg. 125.38).";
              }
            },
          }}
        />

        <Button
          fullWidth={true}
          size="large"
          variant="outlined"
          onClick={handleTokenSelectorToggle}
          className={classNames(
            styles["asset-selector"],
            "d-flex align-items-center justify-content-between mb-3"
          )}
        >
          {loading ? (
            <Typography className="text-center w-100">Loading...</Typography>
          ) : (
            <>
              {
                selectedToken ? (
                  <div className="d-flex align-items-center">
                    <Avatar
                      className={classNames(styles["avatar"])}
                      sx={{ width: 34, height: 34 }}
                      alt={`${selectedToken.name}'s Logo`}
                      src={
                        selectedToken.logo 
                        ? 
                        selectedToken.logo.formats?.thumbnail ? selectedToken.logo.formats?.thumbnail?.url : selectedToken.logo.url
                        : 
                        ""
                      }
                    />
                    <div>
                      <Typography variant="body1">
                        {selectedToken.name.length > 25 ? selectedToken.name.substring(0,25) + "..." : selectedToken.name}
                      </Typography>
                    </div>
                  </div>
                ) : (
                  <div className="text-center w-100">
                    <Typography variant="body1">Please select</Typography>
                  </div>
                )
              }
              <ChevronDown className={classNames(styles["arrow"])} />
            </>
          )}
        </Button>

        {selectedToken && (
          <PurchasePreview
            amount={isValid ? amount : 0}
            symbol={selectedToken.symbol}
            swap={"amount"}
            action="sell"
          />
        )}

        {is_error_visible ? (
          <Alert className="mb-3" severity="error">
            {error}
          </Alert>
        ) : null}

        <Button
          type="submit"
          fullWidth={true}
          disableElevation={true}
          size="large"
          variant="contained"
          disabled={
            loading ||
            !isValid ||
            submitting
          }
        >
          Submit
        </Button>
      </form>
      <Dialog
        onClose={handleTokenSelectorToggle}
        open={tokenSelectorOpen}
        fullWidth={true}
        className={classNames(styles["asset-selector-dialog"])}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pr={1}
        >
          <DialogTitle>Select an asset to sell</DialogTitle>
          <IconButton onClick={handleTokenSelectorToggle}>
            <CloseCircleOutline />
          </IconButton>
        </Box>
        <DialogContent className={classNames(styles["content"])}>
          <MenuList>
            {list.map((token, i) => {
              if (token.symbol === "tether") return null;
              return (
                <MenuItem
                  onClick={() => {
                    setTokenSelectorOpen(false);
                    if (!token.balance) {
                      token.balance = {
                        amount: 0,
                      };
                    }
                    setSelectedToken(token);
                  }}
                  divider={i < list.length - 1 && true}
                  key={`token-${token.symbol}`}
                  className={classNames(styles["item"], "py-2")}
                >
                  <ListItemIcon className="me-3">
                    <Avatar 
                      alt={`${token.name}'s Logo`} 
                      src={
                        token.logo 
                        ? 
                        token.logo.formats?.thumbnail ? token.logo.formats?.thumbnail?.url : token.logo.url
                        : 
                        ""
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={token.name}
                    secondary={token.display_symbol}
                  />
                </MenuItem>
              );
            })}
          </MenuList>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default Sell;
